import { Router } from '@angular/router';
import { TabComponent } from '@amobee/acl-ngx';
import { Component, Input, OnDestroy, OnInit, Output, EventEmitter } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import * as _ from 'lodash';
import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CreativeAdapter } from 'src/adapters/creative-adapter';
import { DurationConst } from 'src/app/data/constants/duration.const';
import { AdProduceService } from 'src/app/services/ad-produce.service';
import { CreativeFormats, CreativeVO } from './../../../../../data/vo/CreativeVO';
import { CampaignStatus } from 'src/app/data/constants/campaign-status.const';
import { getNextUTCDate, getUTCDate } from 'src/whiteLabel/mediaCorps/services/campaign/campaign-validators';

@Component({
  selector: 'ad',
  templateUrl: './ad.component.html',
  styleUrls: ['./ad.component.scss']
})
export class AdComponent implements OnInit, OnDestroy {
  @Input() group: FormGroup;
  @Input() isNew: boolean;
  @Input() planId: number;
  @Input() isCreativeEdit: boolean;
  @Input() campaignId: number;
  @Input() campaignStatus: string;
  @Output() uploadCreativeLater = new EventEmitter<boolean>();
  @Output() durationExist = new EventEmitter<boolean>();

  columns: Array<string> = ['Creative Name', 'Creative Language', ''];
  previewCreative: any;
  selectedData: Array<any> = [];
  dataSettings = {
    idField: 'creativeId',
    textField: 'name',
    closeDropDownOnSelection: false,
    enableCheckAll: false,
    allowSearchFilter: true,
    hideResetButton: true,
    showChipSelection: false,
    noDataAvailablePlaceholderText: 'No creatives found'
  };
  creativeData: Array<CreativeVO> = [];
  selectedCreatives: Array<CreativeVO> = [];
  defaultSelectedCreatives: Array<CreativeVO> = [];
  creativeDropdownSubject: Subject<any> = new Subject();
  creativeDropdownSubscription: Subscription;
  adProduceSub: Subscription;
  hideCreativeWarning: boolean = true;
  disableProduceAd: boolean = false;
  disableSkipCreative: boolean = false;
  showHardLaunchNoCreativeMessage: boolean = false;
  produceTab = {
    title: 'Produce Your Ad with Mediacorp',
    active: false
  };
  durations: Array<any> = [];

  uploadCreativeUrl: string;

  languages = [];
  selectedDuration: any;

  constructor(
    private creativeAdapter: CreativeAdapter,
    private adProduceService: AdProduceService,
    private router: Router) { }

  ngOnInit() {
    this.uploadCreativeUrl = '#/creative/new?isVideoProduce=false&planId=' + this.planId;
    let startDateInUTC = getUTCDate(this.startDateControl.value);
    if (this.isCreativeEdit) {
      if (startDateInUTC <= getNextUTCDate(7)) {
        this.disableSkipCreative = true;
      }
    }
    if (this.campaignStatus !== CampaignStatus.Draft.key && startDateInUTC <= getNextUTCDate(30)) {
      this.disableProduceAd = true;
    }
    _.forEach(DurationConst, (value: any) => {
      this.durations.push(value);
    });

    this.creativeAdapter.getAdDurations().then((adDurations: any) => {
      let tmpDurations = [];
      adDurations.forEach(adDuration => {
        if (adDuration.device_formats.includes('TV_LINEAR')) {
          tmpDurations.push({ value: adDuration.min_length_seconds.toString(), label: adDuration.min_length_seconds + ' Seconds' });
        }
      });
      this.durations = tmpDurations;
      this.updateCreativeDuration();
    });

    if (this.creativesControl.value && this.creativesControl.value.length > 0) {
      this.selectedData = _.map(this.creativesControl.value, (d) => {
        return {
          id: d.creativeId,
          text: d.name,
          disabled: this.isCreativeEdit
        }
      });
      this.selectedCreatives = _.each(this.creativesControl.value, (v) => v.isDisabled = this.isCreativeEdit);
      this.defaultSelectedCreatives = _.clone(this.selectedCreatives);
    }
    this.creativeAdapter.getAllAssignableCreatives().then(data => {
      this.creativeData = data;
      if (this.selectedCreatives.length) {
        this.selectedCreatives = _.intersectionBy(data, this.selectedCreatives, 'creativeId');
        this.creativesControl.patchValue(this.selectedCreatives);
        if (this.isCreativeEdit) {
          this.selectedCreatives = _.each(this.creativesControl.value, (v) => v.isDisabled = this.isCreativeEdit);
        }
      }
      if (this.isCreativeEdit) {
        this.creativeData = _.each(this.creativeData, (v) => {
          if (this.defaultSelectedCreatives.findIndex((a) => a.creativeId === v.creativeId) > -1) {
            v.isDisabled = true;
          }
        });
      }
      if (this.campaignStatus !== CampaignStatus.Draft.key && startDateInUTC <= getNextUTCDate(3, true) && this.creativesControl.value.length === 0) {
        this.showHardLaunchNoCreativeMessage = true;
      }
    });
    this.creativeDropdownSubscription = this.creativeDropdownSubject.pipe(debounceTime(300)).subscribe(() => {
      this.creativeAdapter.getAllAssignableCreatives().then(data => {
        this.creativeData = data;
        if (this.isCreativeEdit) {
          this.creativeData = _.each(this.creativeData, (v) => {
            if (this.defaultSelectedCreatives.findIndex((a) => a.creativeId === v.creativeId) > -1) {
              v.isDisabled = true;
            }
          });
        }
      });
    });
    this.adProduceSub = this.adProduceService.produceCreativeObs.subscribe((val) => {
      if (val.length === 0) {
        this.produceTab.active = false;
      }
    });
    if (this.skipCreativeValue) {
      this.creativesControl.disable();
      this.produceCreative.disable();
      this.updateCreativeDuration();
    } else {
      this.adDurationControl.patchValue(null);
    }
  }

  updateCreativeDuration() {
    this.selectedDuration = this.adDurationControl.value ? _.filter(this.durations, x => x.value === this.adDurationControl.value) : _.filter(this.durations, x => x.value === DurationConst.SECONDS_30.value);
    this.adDurationChange(this.selectedDuration[0]);
  }

  handleOnTabChange(tab: TabComponent) {
    // Need to update the object as well in case of user clicking
    if (tab.title === this.produceTab.title) {
      if (this.isCreativeEdit && !this.disableProduceAd) {
        this.router.navigateByUrl(`/creative/new?isVideoProduce=true&&campaignId=${this.campaignId}`).catch((e) => {
          console.error(e);
        });
      }
      this.produceTab.active = true;
    } else {
      this.produceTab.active = false;
    }
  }

  ngOnDestroy(): void {
    if (this.creativeDropdownSubscription) {
      this.creativeDropdownSubscription.unsubscribe();
    }
    if (this.adProduceSub) {
      this.adProduceSub.unsubscribe();
    }
  }

  reloadCreativeData() {
    this.creativeDropdownSubject.next();
  }

  onClickProduceWithUsLink() {
    this.produceTab.active = true;
  }

  onCreativeSelectDeselect(event, isDeselect: boolean) {
    this.creativesControl.markAsTouched();
    if (event) {
      let selectedData = _.map(this.selectedData, (d) => {
        return {
          creativeId: d.id,
          name: d.text
        }
      });
      this.selectedCreatives = _.intersectionBy(this.creativeData, selectedData, 'creativeId');
      this.creativesControl.patchValue(this.selectedCreatives);
      if (isDeselect && this.previewCreative && this.previewCreative.creativeId == event.creativeId) {
        this.previewCreative = undefined;
      }
    }
  }

  selectPreview(creativeFormat: Array<CreativeFormats>, creativeId: number) {
    this.previewCreative = {
      cdnUrl: undefined,
      creativeId: creativeId
    };

    if (_.size(creativeFormat)) {
      let compatibelFormat: CreativeFormats = _.find(creativeFormat, (item: CreativeFormats) => {
        return item.formatType.toLowerCase() === 'mp4';
      });
      if (compatibelFormat) {
        this.previewCreative.cdnUrl = compatibelFormat.cdnUrl;
      }
    }
  }

  removeCreative(creative: CreativeVO) {
    if (this.isDisabled || this.skipCreativeValue || creative.isDisabled) {
      return;
    }
    this.selectedCreatives = _.filter(this.selectedCreatives, c => c.creativeId !== creative.creativeId);
    this.selectedData = _.filter(this.selectedData, c => c.id !== creative.creativeId);
    this.creativesControl.patchValue(this.selectedCreatives);
  }

  onUploadCreativeLaterChange($event) {
    if ($event.target) {
      if ($event.target.checked) {
        this.skipCreativeControl.patchValue(true);
        this.selectedCreatives = [];
        this.creativesControl.patchValue([]);
        this.creativesControl.disable();
        while (this.produceCreative.length !== 0) {
          this.produceCreative.removeAt(0)
        }
        this.produceCreative.disable();
        this.uploadCreativeLater.emit(this.skipCreativeValue);
        this.updateCreativeDuration();
      } else {
        this.skipCreativeControl.patchValue(false);
        this.creativesControl.enable();
        this.produceCreative.enable();
        this.uploadCreativeLater.emit(this.skipCreativeValue);
      }
      this.hideCreativeWarning = !$event.target.checked;
    }
  }

  adDurationChange(selectedAdDuration) {
    if (selectedAdDuration === undefined) {
      //the existing campaign duration in no longer exist in the loaded durations
      this.durationExist.emit(false);
    } else {
      this.adDurationControl.patchValue(selectedAdDuration.value);
      this.durationExist.emit(true);
    }
  }

  get creativesControl(): FormControl {
    return this.group.get("creatives") as FormControl;
  }

  get produceCreative(): FormArray {
    return this.group.get('produceCreative') as FormArray;
  }

  get skipCreativeControl(): FormControl {
    return this.group.get("skipCreative") as FormControl;
  }

  get adDurationControl(): FormControl {
    return this.group.get('adDuration') as FormControl;
  }

  get startDateControl(): FormControl {
    return this.group.get('startDate') as FormControl;
  }

  get skipCreativeValue(): boolean {
    return this.skipCreativeControl.value;
  }

  get isDisabled(): boolean {
    return this.group.disabled;
  }

}
